import React from 'react';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/ai-native-semantics-approach.jpg
import aiNativeSemanticsImg from '../../../assets/images/blogs/ai-native-semantics-approach.jpg';

const AiNativeSemanticsApproach = () => (
    <BlogPost
        title="AI-Native Semantics: A Different Approach"
        subtitle="AI that discovers, reasons, acts - but you can trust."
        category="Architecture"
        categoryIcon={faChartLine}
        color="#003366"
        date="February 2026"
        readTime="6 min read"
        image={aiNativeSemanticsImg}
        imageAlt="AI-native semantics - bridging information and execution with dynamism and control"
        ctaHeading="Ready for trustworthy AI semantics?"
        ctaText="Book a discovery call and we'll show you how AI-native semantics enables agents that discover, reason, and act - with full auditability."
        seoDescription="How AI-native semantics bridges the information and execution world with dynamism and control, demonstrated through a finance trading example."
        seoKeywords="AI semantics, agentic AI, finance AI, trading AI, Data Object Graphs, semantic reasoning, Nebulyx"
        slug="ai-native-semantics-approach"
    >
        <p>
            I have been working with natural language, AI and graph-based solutions for
            nearly 20 years, with real-world use-cases.
        </p>

        <p>
            I am currently working on a number of high-information AI/Agentic use-cases.
        </p>

        <p>
            One big challenge I've found is bridging the information and execution world
            with dynamism and control, whilst dealing with the unknown quickly and gracefully.
        </p>

        <h3>An Example in Finance Trading</h3>

        <p>
            A trader asks: "What's our exposure if the EU implements these proposed tariffs
            on Chinese EV components?"
        </p>

        <p>
            Simple question with a complex-to-obtain answer.
        </p>

        <p>
            The system needs to understand:
        </p>

        <p>
            • <strong>"these proposed tariffs"</strong> - still in draft and changing daily<br />
            • <strong>"Chinese EV components"</strong> - across a supply chain spanning
            multiple tiers<br />
            • <strong>"Our exposure"</strong> - given current positions, contracts, hedges
        </p>

        <p>
            Understanding is not enough - the system needs to act.
        </p>

        <h3>The Agentic Flow</h3>

        <p>
            An agent receives the query. It does not just search - it orchestrates:
        </p>

        <p>
            • <strong>External sources:</strong> news feeds, market data, supply chain APIs<br />
            • <strong>Internal systems:</strong> trading positions, contracts, supplier
            records, hedges<br />
            • <strong>Model Execution:</strong> scenario analysis, impact forecasts, risk scores
        </p>

        <p>
            Each call updates the semantic graph (as a Data Object Graph) grounded in current
            reality. The agent discovers what it needs to know, not just what it already knows.
        </p>

        <h3>Why AI-Native Semantics</h3>

        <p>
            Traditional approaches define meaning at design time. Experts curate concepts
            and relationships. Valuable but slow with edge cases appearing faster than
            governance responds.
        </p>

        <p>
            <strong>AI-native semantics work differently:</strong>
        </p>

        <p>
            <strong>Runtime not design-time:</strong> meaning extracted dynamically from
            source material as it arrives (with SME oversight).
        </p>

        <p>
            <strong>Context-aware:</strong> "exposure" means different things to trading
            vs risk vs compliance. Multiple frames, right one activated.
        </p>

        <p>
            <strong>Open world:</strong> new entities, regulations, relationships emerge
            constantly. Discovers, grounds, flags uncertainty.
        </p>

        <p>
            <strong>AI-friendly encoding:</strong> semantics as graphs and embeddings AI
            models reason over directly. Native representations for machine understanding.
        </p>

        <p>
            <strong>Coupled to execution:</strong> no translation layer. Understanding
            flows straight into action.
        </p>

        <h3>How It Works</h3>

        <p>
            <strong>Semantic Reasoning Engine:</strong> dynamic, contextual understanding
            encoded for AI-native consumption.
        </p>

        <p>
            <strong>Agent Execution Engine:</strong> orchestrates Data Products as tools
            via the Tool Manager.
        </p>

        <p>
            <strong>State and Context Manager:</strong> captures every query, result,
            decision. Full provenance.
        </p>

        <p>
            Three components, one integrated flow with feedback and learning.
        </p>

        <h3>The Result</h3>

        <p>
            1. The agent understood the question<br />
            2. Discovered entities dynamically<br />
            3. Queried the sources<br />
            4. Ran models (as Data Products)<br />
            5. Synthesised an answer grounded in current state
        </p>

        <blockquote className="blog-post-quote">
            "All auditable, explainable and trustworthy. AI that discovers, reasons, acts -
            but you can trust."
        </blockquote>

        <p>
            🎯
        </p>
    </BlogPost>
);

export default AiNativeSemanticsApproach;
